import {Component, Input, input, InputSignal, OnDestroy, OnInit, signal} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormSubmittedEvent,
  ReactiveFormsModule,
  TouchedChangeEvent
} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';
import {Theme} from '@core/service/browser';

export interface ValidationError {
  key: string;
  error: any;
}

@Component({
  selector: 'app-abstract-field',
  imports: [
    ReactiveFormsModule
  ],
  template: '',
  styleUrls: ['./abstract-field.component.scss'],
})
export abstract class AbstractFieldComponent<C extends AbstractControl> implements OnInit, OnDestroy {

  protected static fieldCounter = 0;

  public control = input.required<C>();
  public label = input<string>();
  public showLabel = input<boolean>(true);
  public description = input<string>();
  public placeholder = input<string>();
  public errors = signal<ValidationError[]>([]);
  public dependsOn = input<AbstractControl>();
  public translate = input<boolean>(false);
  public optional = input<boolean>(false);
  public theme = input<Theme | null>(null);
  public large = input(false);
  public small = input(false);
  // public enableCond = input<(field: FormControl) => boolean>();
  @Input()
  public enableCond?: (control: AbstractControl) => boolean;

  public readonly fieldId: string;

  public constructor() {
    this.fieldId = `field${AbstractFieldComponent.fieldCounter++}`;
  }

  protected readonly destroy$: Subject<void> = new Subject();

  public ngOnInit(): void {
    this.control().statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(_status => {
      if (this.control().touched) {
        this.errors.set(this.fetchErrors());
      }
    });
    this.control().events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (this.control().enabled && event instanceof TouchedChangeEvent) {
        this.errors.set(this.fetchErrors());
      }
    });
    const rootForm = this.getRootForm();
    if (rootForm) {
      rootForm.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (this.control().enabled && event instanceof FormSubmittedEvent) {
          this.errors.set(this.fetchErrors());
        }
      });
    }

    const dependency = this.dependsOn();
    if (dependency) {
      dependency.statusChanges.pipe(
        takeUntil(this.destroy$)
      ).subscribe(_status => {
        this.refreshDependency();
      });
      this.refreshDependency();
    }

    const hasEnableCond = !!this.enableCond;
    if (hasEnableCond) {
      if (!dependency) {
        console.warn(`[${this.fieldId}] enableCond without dependency.`);
      } else {
        dependency?.valueChanges.pipe(
          takeUntil(this.destroy$)
        ).subscribe(_status => {
          this.refreshDependency();
        });
        this.refreshDependency();
      }
    }

  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get typedControl(): InputSignal<C> {
    return this.control as InputSignal<C>;
  }

  private fetchErrors(): ValidationError[] {
    const validationErrors = this.control().errors;
    if (this.control().disabled || !validationErrors) {
      return [];
    }
    return Object.entries(validationErrors).map(([key, error]) => ({key: key, error: error}));
  }

  private getRootForm(): FormGroup | FormArray | null {
    let parent: FormGroup | FormArray | null = this.control().parent;
    while (parent) {
      if (!parent.parent) {
        return parent;
      }
      parent = parent.parent;
    }
    return null;
  }

  private refreshDependency(): void {
    const dependency = this.dependsOn();
    const enableCond = this.enableCond;
    if (dependency) {
      const enable = enableCond ? enableCond(dependency) : !!dependency?.valid;
      if (enable) {
        this.control()?.enable();
      } else {
        this.control()?.disable();
      }
    }
  }


}
